import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { api, type Drueckteil } from '../lib/api';
import BeistelteileInputs, { type BeistelteilDraft } from '../components/BeistelteileInputs';

export function NeuerAuftrag() {
  const nav = useNavigate();
  const kunden     = useApi(() => api.lookup.kunden(), []);
  const users      = useApi(() => api.lookup.users(), []);
  const standWerte = useApi(() => api.lookup.standWerte(), []);

  const [form, setForm] = useState({
    bestellnr: '',
    dv_nummer: '',
    position: '',
    kunde_id: '',
    sachbearbeiter_id: '',
    stueck: 1,
    masse_bezeichnung: '',
    material: '',
    baugruppenname: '',
    teile_geschnitten: '',
    lieferdatum: '',
    zeit_pro_stueck: '',
    stand_id: '',
    priority: 'normal' as 'normal' | 'eilig' | 'kritisch',
    kommentar: '',
    vorkasse: false,
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState<string | null>(null);
  const [beistelteile, setBeistelteile] = useState<BeistelteilDraft[]>([]);

  const update = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!form.bestellnr || !form.lieferdatum || !form.stueck) {
      setError('Pflichtfelder fehlen: Bestellnr, Liefertermin, Stückzahl');
      return;
    }

    setSaving(true);
    try {
      const body: Partial<Drueckteil> = {
        bestellnr:         form.bestellnr,
        dv_nummer:         form.dv_nummer || null,
        position:          form.position || null,
        kunde_id:          form.kunde_id ? Number(form.kunde_id) : null,
        sachbearbeiter_id: form.sachbearbeiter_id ? Number(form.sachbearbeiter_id) : null,
        stueck:            Number(form.stueck),
        masse_bezeichnung: form.masse_bezeichnung || null,
        material:          form.material || null,
        baugruppenname:    form.baugruppenname || null,
        lieferdatum:       form.lieferdatum,
        zeit_pro_stueck:   form.zeit_pro_stueck ? Number(form.zeit_pro_stueck) : null,
        stand_id:          form.stand_id ? Number(form.stand_id) : null,
        priority:          form.priority,
        kommentar:         form.kommentar || null,
        vorkasse:          form.vorkasse ? 1 : 0,
      };
      // Beistelteile-Validierung
      const validBeistelteile = beistelteile.filter(b => b.bezeichnung.trim());
      const invalid = beistelteile.find(b => !b.bezeichnung.trim() && (b.zeichnungs_nr || b.erwartet_bis || b.notes));
      if (invalid) {
        setError('Beistelteil ohne Bezeichnung gefunden — bitte ergänzen oder entfernen');
        return;
      }

      const res = await api.drueckteile.create(body);
      const newId = res.data.id;

      if (validBeistelteile.length > 0) {
        await Promise.all(
          validBeistelteile.map(b =>
            api.beistelteile.create({
              auftrag_modul: 'drueckteile',
              auftrag_id: newId,
              bezeichnung: b.bezeichnung.trim(),
              zeichnungs_nr: b.zeichnungs_nr.trim() || null,
              erwartete_stueckzahl: b.erwartete_stueckzahl,
              erwartet_bis: b.erwartet_bis || null,
              notes: b.notes.trim() || null,
            })
          )
        );
      }

      nav(`/drueckteile/${newId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8 max-w-4xl">
      <Link to="/drueckteile" className="text-sm text-brand hover:underline">← Zurück</Link>

      <header className="mt-4 mb-6">
        <div className="text-xs uppercase text-slate-500">Produktion › Drückteile</div>
        <h1 className="text-2xl font-bold text-slate-900">Neuer Auftrag</h1>
      </header>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-4 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-slate-200 p-6 space-y-6">
        <section>
          <h2 className="font-semibold text-slate-900 mb-3">Auftragsdaten</h2>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Bestellnummer *">
              <input value={form.bestellnr} onChange={e => update('bestellnr', e.target.value)}
                     className="input" required placeholder="z.B. 4133885" />
            </Field>
            <Field label="DV-Nummer">
              <input value={form.dv_nummer} onChange={e => update('dv_nummer', e.target.value)}
                     className="input" placeholder="aus externem System" />
            </Field>
            <Field label="Position">
              <input value={form.position} onChange={e => update('position', e.target.value)}
                     className="input" placeholder="z.B. 1v2" />
            </Field>

            <Field label="Kunde">
              <select value={form.kunde_id} onChange={e => update('kunde_id', e.target.value)} className="input">
                <option value="">— bitte wählen —</option>
                {kunden.data?.data.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
              </select>
            </Field>
            <Field label="Sachbearbeiter">
              <select value={form.sachbearbeiter_id} onChange={e => update('sachbearbeiter_id', e.target.value)} className="input">
                <option value="">— bitte wählen —</option>
                {users.data?.data.filter(u => u.kurzel !== 'SYS').map(u =>
                  <option key={u.id} value={u.id}>{u.name}</option>
                )}
              </select>
            </Field>
            <Field label="Priorität">
              <select value={form.priority} onChange={e => update('priority', e.target.value as typeof form.priority)} className="input">
                <option value="normal">Normal</option>
                <option value="eilig">Eilig</option>
                <option value="kritisch">Kritisch</option>
              </select>
            </Field>
          </div>
        </section>

        <section className="border-t border-slate-100 pt-6">
          <h2 className="font-semibold text-slate-900 mb-3">Teilbeschreibung</h2>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Maße / Bezeichnung" className="col-span-2">
              <input value={form.masse_bezeichnung} onChange={e => update('masse_bezeichnung', e.target.value)}
                     className="input" placeholder="z.B. 1763-1400-200-452-45°" />
            </Field>
            <Field label="Material (Zeichnung)">
              <input value={form.material} onChange={e => update('material', e.target.value)}
                     className="input" placeholder="z.B. 1.4410 s4" />
            </Field>

            <Field label="Stück *">
              <input type="number" min={1} value={form.stueck}
                     onChange={e => update('stueck', parseInt(e.target.value) || 1)}
                     className="input" required />
            </Field>
            <Field label="Zeit / Stück (h)">
              <input type="number" step="0.25" min={0} value={form.zeit_pro_stueck}
                     onChange={e => update('zeit_pro_stueck', e.target.value)}
                     className="input" placeholder="z.B. 0.5" />
            </Field>
            <Field label="Baugruppe">
              <input value={form.baugruppenname} onChange={e => update('baugruppenname', e.target.value)}
                     className="input" placeholder="z.B. Konus-DU" />
            </Field>
          </div>
        </section>

        <section className="border-t border-slate-100 pt-6">
          <h2 className="font-semibold text-slate-900 mb-3">Termin & Status</h2>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Liefertermin *">
              <input type="date" value={form.lieferdatum}
                     onChange={e => update('lieferdatum', e.target.value)}
                     className="input" required />
              <div className="text-xs text-slate-500 mt-1">
                Intern (-3 Tage) wird automatisch berechnet
              </div>
            </Field>
            <Field label="Stand">
              <select value={form.stand_id} onChange={e => update('stand_id', e.target.value)} className="input">
                <option value="">— offen / keiner —</option>
                {standWerte.data?.data.map(s => <option key={s.id} value={s.id}>{s.wert}</option>)}
              </select>
            </Field>
            <Field label="Zahlung">
              <label className="inline-flex items-center gap-2 mt-2 text-sm text-slate-700">
                <input type="checkbox" checked={form.vorkasse} onChange={e => update('vorkasse', e.target.checked)} />
                🔒 Vorkasse — Auftrag gesperrt bis Zahlungseingang
              </label>
            </Field>
          </div>
        </section>

        <section className="border-t border-slate-100 pt-6">
          <Field label="Kommentar">
            <textarea value={form.kommentar} onChange={e => update('kommentar', e.target.value)}
                      className="input" rows={3}
                      placeholder="Notizen, Eilzuschlag, Besonderheiten …" />
          </Field>
        </section>

        <BeistelteileInputs items={beistelteile} onChange={setBeistelteile} />

        <div className="flex justify-end gap-3 border-t border-slate-100 pt-6">
          <Link to="/drueckteile" className="btn-secondary">Abbrechen</Link>
          <button type="submit" disabled={saving} className="btn-primary disabled:opacity-50">
            {saving ? 'Speichert …' : '✓ Speichern'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children, className }: {
  label: string; children: React.ReactNode; className?: string;
}) {
  return (
    <div className={className}>
      <label className="text-xs text-slate-600 font-medium block mb-1">{label}</label>
      {children}
    </div>
  );
}
